import { useMemo } from "react";
import styled from "styled-components";
import { CaretDownIcon } from "../../../../../components/Icons/Icons";
import { H3 } from "../../../../../components/Typography/Typography";
import { AttributeValueDisplay } from "../../../ProductDetailPage/ProductOverview/product_overview.utils";
import type { AttributeSchema, PIMProduct } from "../../../../../types/types.PIM";
import {
  getGroupCompareRows,
  MISSING_VALUE_DISPLAY,
} from "./CompareProducts.util";

const Section = styled.div`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")} 0;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const Caret = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(0deg)" : "rotate(-90deg)"};
  transition: transform 0.15s ease-out;
`;

const Matrix = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const AttributeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  min-width: 0;
  overflow: hidden;
`;

const AttributeLabel = styled.div`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
`;

type CompareGroupSectionProps = {
  groupName: string;
  displayName: string;
  products: PIMProduct[];
  expanded: boolean;
  onToggle: () => void;
};

/**
 * Scope attribute identity per product column so inputs (e.g. toggles) that key
 * off attribute.name/id do not collide across the comparison matrix.
 */
function scopeAttributeForProduct(
  attribute: AttributeSchema,
  productId: string
): AttributeSchema {
  return {
    ...attribute,
    // Keep real id for value identity, but make name unique per column for DOM ids.
    name: `${attribute.name}__${productId}`,
  };
}

export function CompareGroupSection({
  groupName,
  displayName,
  products,
  expanded,
  onToggle,
}: CompareGroupSectionProps) {
  const rows = useMemo(
    () => getGroupCompareRows(products, groupName),
    [products, groupName]
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeader type="button" onClick={onToggle}>
        <Caret $expanded={expanded}>
          <CaretDownIcon width={16} height={16} />
        </Caret>
        <H3 style={{ margin: 0 }}>{displayName}</H3>
      </SectionHeader>
      {expanded && (
        <div>
          {rows.map((row) => (
            <Matrix key={row.attributeName} $cols={products.length}>
              {products.map((product) => {
                const attribute = row.attributesByProductId[product.id];
                return (
                  <AttributeBlock
                    key={`${product.id}-${row.attributeName}`}
                    data-product-id={product.id}
                    data-attribute={row.attributeName}
                  >
                    <AttributeLabel title={row.displayName}>
                      {row.displayName}
                    </AttributeLabel>
                    {attribute ? (
                      <AttributeValueDisplay
                        attribute={scopeAttributeForProduct(
                          attribute,
                          product.id
                        )}
                        isPortfolio={false}
                        max_line_clamp={4}
                      />
                    ) : (
                      <EmptyValue>{MISSING_VALUE_DISPLAY}</EmptyValue>
                    )}
                  </AttributeBlock>
                );
              })}
            </Matrix>
          ))}
        </div>
      )}
    </Section>
  );
}
